import { SafetyReviewDismissReason } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import Label from '@cobliteam/ui/form/Label';
import Icon from '@cobliteam/ui/icons';
import { useCallback, useEffect, useState } from 'react';
import { useDismissReasonLabels } from '../../../utils/mediaUtils';

export interface MultiSelectSafetyDismissReasonProps {
  isOpen?: boolean;
  size?: 'sm' | 'md';
  selectedOptions?: SafetyReviewDismissReason[];
  onChange: (selectedOptions: SafetyReviewDismissReason[]) => void;
}

const MultiSelectSafetyDismissReason = ({ isOpen, size = 'md', selectedOptions = [], onChange }: MultiSelectSafetyDismissReasonProps) => {
  const { t } = useTranslation('multiselects');
  const labels = useDismissReasonLabels();

  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState(selectedOptions || []);

  const hasSameElements = selectedOptions?.length === draft?.length && selectedOptions.every((value) => draft.find((d) => d === value));

  const onClick = useCallback(() => {
    setDraft(selectedOptions || []);
  }, [selectedOptions]);

  const onChangeSelect = (type: SafetyReviewDismissReason) => {
    const isSelected = draft.find((d) => d === type);
    const updatedSelected = isSelected ? draft.filter((d) => d !== type) : [...draft, type];
    setDraft(updatedSelected);
  };

  const handleClose = useCallback(() => {
    setForceClosed(true);
    setDraft([]);
  }, []);

  const handleApply = useCallback(() => {
    onChange(draft);
    handleClose();
  }, [draft, handleClose, onChange]);

  useEffect(() => setForceClosed(!isOpen), [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  const data = [
    SafetyReviewDismissReason.NO_EVENT_OR_OCCURRENCE,
    SafetyReviewDismissReason.WRONG_EVENT_OR_OCCURRENCE,
    SafetyReviewDismissReason.BLURRY_OR_LOW_QUALITY,
    SafetyReviewDismissReason.EVENT_NOT_INTERESTING,
  ];

  return (
    <Dropdown
      portal={false}
      onOpenChange={onClick}
      forceOpen={isOpen}
      forceClose={forceClosed}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='ghost' size='sm' disabled={draft?.length === 0} onClick={() => setDraft([])}>
              {t('safety_review_status.clear_selection')}
            </Button>
          </Dropdown.Header>
          <Dropdown.Body className='gap-2 px-2'>
            {data.map((item) => (
              <div className='mb-2 flex items-start gap-2' key={item}>
                <Checkbox className='mt-0.5' id={item} checked={draft.includes(item)} onCheckedChange={() => onChangeSelect(item)} />
                <Label htmlFor={item}>{labels[item]}</Label>
              </div>
            ))}
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button full variant='ghost' size='sm' onClick={handleClose}>
                {t('safety_review_status.cancel')}
              </Button>
              <Button full variant='contained' size='sm' color='primary' onClick={handleApply} disabled={hasSameElements}>
                {t('safety_review_status.apply')}
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='ghost' startIcon={<Icon name='file-question' />}>
        {t('safety_review_dismiss_reason.title')} {(selectedOptions?.length || 0) > 0 ? `(${selectedOptions?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectSafetyDismissReason;
